import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCourseBySlug } from '../services/ecourseApi';
import api from '../services/api';
import authService from '../services/auth';
import Header from '../components/layout/Header';
import NavigationButton from '../components/layout/Navigation';
import DynaQRISModal from '../components/common/DynaQRISModal';
import { getPublicPaymentConfig, generateDynaQRIS } from '../services/paymentApi';
import '../styles/Body.css';

const getCsrfToken = () => {
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];
  return cookieValue;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'tidak ada';
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatIDR = (amount) => {
  return 'Rp. ' + new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
  }).format(amount);
};

const bankAccounts = {
  bsi: {
    name: 'bsi',
    number: '2220606662',
    fullName: 'Bank Syariah Indonesia',
    logo: '/images/bsi-logo.png',
    owner: 'Barakah Economy Community',
  },
  qris: {
    name: 'qris',
    number: 'QRIS BAE COMMUNITY',
    fullName: 'QRIS',
    logo: '/images/qris-bae2.png',
    owner: 'BAE COMMUNITY',
    isQRIS: true,
  },
};

const EcoursePaymentConfirmation = () => {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [profile, setProfile] = useState({ name_full: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [formData, setFormData] = useState({
    accountName: '',
    sourceBank: '',
    sourceAccount: '',
    transferDate: new Date().toISOString().split('T')[0],
  });
  const [selectedBank, setSelectedBank] = useState('bsi');
  const [isSuccess, setIsSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // DynaQRIS State
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [showDynaModal, setShowDynaModal] = useState(false);
  const [qrisData, setQrisData] = useState(null);
  const [generatingQris, setGeneratingQris] = useState(false);

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {};

  // Fetch course & payment config
  useEffect(() => {
    const fetchCourse = async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const [res, configRes] = await Promise.all([
          getCourseBySlug(slug),
          getPublicPaymentConfig().catch(() => null)
        ]);
        if (res?.data) {
          setCourse(res.data);
          setPaymentConfig(configRes);
        } else {
          setFetchError(true);
        }
      } catch (err) {
        console.error("Error fetching course and config:", err);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [slug]);

  const handleGenerateDynaQRIS = async (amtOverride) => {
    const targetAmt = amtOverride || course?.price;
    if (!targetAmt) return;
    setGeneratingQris(true);
    try {
      const res = await generateDynaQRIS({ amount: targetAmt, type: 'ecourse', add_unique_code: false });
      if (res.error) {
        alert(res.error);
      } else {
        setQrisData(res);
        setShowDynaModal(true);
      }
    } catch (err) {
      console.error(err);
      alert('Gagal menghasilkan QRIS Dinamis.');
    } finally {
      setGeneratingQris(false);
    }
  };

  const handleDynaSuccess = (res) => {
    setShowDynaModal(false);
    setIsSuccess(true);
    alert('Pembayaran berhasil! Selamat belajar.');
    navigate(`/kelas/${course?.slug || slug}`);
  };

  // Fetch profile only once on mount if user exists
  useEffect(() => {
    if (user && user.id) {
      const fetchProfile = async () => {
        try {
          const profileData = await authService.getProfile(user.id);
          setProfile(profileData);
        } catch {
          setProfile({ name_full: '' });
        }
      };
      fetchProfile();
    }
    // eslint-disable-next-line
  }, []); // <-- empty dependency array, runs only once

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Ukuran file terlalu besar. Maksimal 5MB.');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
        alert('Format file tidak didukung. Hanya JPG, PNG, JPEG, dan WebP yang diperbolehkan.');
        return;
      }
      setSelectedFile(file);
      const fileReader = new FileReader();
      fileReader.onload = () => setPreviewUrl(fileReader.result);
      fileReader.readAsDataURL(file);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
      .then(() => alert(`${type} berhasil disalin!`))
      .catch(err => console.error('Failed to copy: ', err));
  };

  const handleBankChange = (e) => {
    setSelectedBank(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const csrfToken = getCsrfToken();

    if (!selectedFile) {
      alert('Mohon upload bukti transfer');
      return;
    }
    if (!formData.sourceBank || (!selectedBankInfo.isQRIS && !formData.sourceAccount) || !formData.transferDate) {
      alert('Mohon lengkapi semua data yang diperlukan.');
      return;
    }

    // Prepare payment confirmation data
    const paymentData = new FormData();
    paymentData.append('amount', course.price);
    paymentData.append('user_name', profile.name_full || user.username || '');
    paymentData.append('course_id', course.id);
    paymentData.append('course_title', course.title);
    paymentData.append('payment_method', selectedBank);
    paymentData.append('source_bank', formData.sourceBank);
    paymentData.append('source_account', formData.sourceAccount);
    paymentData.append('transfer_date', formData.transferDate);
    paymentData.append('proof_file', selectedFile);

    let authToken = user.access;

    const headers = {
      'Content-Type': 'multipart/form-data',
      'X-CSRFToken': csrfToken,
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const sourceAccountInfo = selectedBankInfo.isQRIS
      ? ''
      : `, dengan No. Rekening ${formData.sourceAccount}`;

    // WhatsApp message
    const message = encodeURIComponent(
      `*Konfirmasi Pembayaran Ecourse*%0A------------------------------------%0A` +
      `Bismillah..%0A` +
      `Pada hari ini,%0A` +
      `Tanggal ${formatDate(formData.transferDate)}%0A` +
      `Saya ${formData.accountName || profile.name_full || user.username || ''} telah melakukan pembayaran untuk kelas ${course.title}%0A` +
      `dengan nominal Rp ${formatIDR(course.price)} melalui ${selectedBankInfo.fullName}%0A%0A` +
      `Saya mengirim pembayaran dari Bank ${formData.sourceBank}${sourceAccountInfo}%0A` +
      `------------------------------------%0A%0A` +
      `Bukti transfer telah saya upload, mohon konfirmasi.%0A` +
      `Semoga bermanfaat dan menjadi amal ibadah.`
    );

    try {
      // Replace with your actual backend endpoint for ecourse payment confirmation
      const response = await api.post(
        '/courses/enrollments/upload-proof/',
        paymentData
      );

      if (response.status === 201 || response.status === 200) {
        // Open WhatsApp with prepared message
        window.open(`https://wa.me/6285643848251?text=${message}`, '_blank');
        // Navigate to course page
        navigate(`/kelas/${course.slug}`);
      } else {
        alert('Gagal mengkonfirmasi pembayaran. Silakan coba lagi.');
      }
    } catch (error) {
      console.error('Error confirming payment:', error.response?.data || error.message);
      alert('Terjadi kesalahan saat mengkonfirmasi pembayaran. Silakan coba lagi.');
    }
  };

  if (loading) {
    return (
      <div className="body min-h-screen bg-slate-50">
        <Header />
        <div className="flex flex-col items-center justify-center py-28 px-4 text-center">
          <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-4" />
          <h2 className="text-base font-bold text-slate-800">Menyiapkan Informasi Pembayaran...</h2>
          <p className="text-xs text-slate-500 mt-1">Sedang memuat data kelas e-course...</p>
        </div>
        <NavigationButton />
      </div>
    );
  }

  if (fetchError || !course) {
    return (
      <div className="body min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-md mx-auto py-24 px-4 text-center">
          <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="material-icons text-3xl">error_outline</span>
          </div>
          <h2 className="text-lg font-bold text-slate-800">Kelas Tidak Ditemukan</h2>
          <p className="text-xs text-slate-500 mt-1 mb-6">
            Data kelas yang ingin Anda bayar tidak ditemukan atau sudah tidak aktif.
          </p>
          <button
            onClick={() => navigate('/academy')}
            className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-md"
          >
            Jelajahi Kelas Lainnya
          </button>
        </div>
        <NavigationButton />
      </div>
    );
  }

  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_API_BASE_URL || ''}${url}`;
  };

  const customBankInfo = course && course.own_bank_status === 'approved' ? {
    bsi: {
      name: course.own_bank_name ? course.own_bank_name.toLowerCase() : 'rekening',
      number: course.own_bank_account,
      fullName: course.own_bank_name,
      logo: '/images/bsi-logo.png',
      owner: course.own_bank_holder,
    },
    qris: {
      name: 'qris',
      number: `QRIS ${course.own_bank_holder}`,
      fullName: 'QRIS',
      logo: course.own_qris_image ? getMediaUrl(course.own_qris_image) : '/images/qris-bae2.png',
      owner: course.own_bank_holder,
      isQRIS: true,
    }
  } : bankAccounts;

  const selectedBankInfo = customBankInfo[selectedBank];

  return (
    <div className="body">
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-md">
        {/* Thank you message */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-medium text-gray-700">
            Terimakasih, <span className="text-green-600">{profile.name_full || user.username}</span>
          </h1>
          <p className="text-gray-600">
            atas pembayaran Anda untuk kelas:
          </p>
          <h2 className="text-2xl font-bold mt-2 mb-6">{course.title}</h2>
        </div>

        {/* QRIS Section if active mode is dynaqris */}
        {paymentConfig?.active_mode === 'dynaqris' && (
          <div className="bg-gradient-to-r from-emerald-600 to-green-700 text-white rounded-2xl p-6 mb-6 shadow-xl text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2">
              <span className="material-icons text-sm">qr_code_2</span>
              <span>Metode Pembayaran QRIS Otomatis</span>
            </div>
            <h3 className="text-xl font-bold mb-1">Scan QRIS Pembayaran</h3>
            <p className="text-xs text-emerald-100 mb-4">
              Pembayaran e-course terverifikasi otomatis dan memberikan akses materi secara langsung.
            </p>
            <button
              onClick={() => handleGenerateDynaQRIS()}
              disabled={generatingQris}
              className="w-full bg-white text-emerald-800 font-bold py-3.5 px-6 rounded-2xl shadow hover:bg-emerald-50 transition flex items-center justify-center gap-2"
            >
              <span className="material-icons text-lg">qr_code_scanner</span>
              <span>{generatingQris ? 'Membuat QRIS...' : 'Tampilkan QRIS E-Course'}</span>
            </button>
          </div>
        )}

        <DynaQRISModal
          isOpen={showDynaModal}
          onClose={() => setShowDynaModal(false)}
          qrisData={qrisData}
          transactionType="ecourse"
          amount={course?.price}
          onPaymentSuccess={handleDynaSuccess}
        />

        {/* Manual Transfer only if mode is NOT dynaqris */}
        {paymentConfig?.active_mode !== 'dynaqris' && (
          <>
            {/* Bank selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pilih Bank Tujuan
              </label>
              <select
                value={selectedBank}
                onChange={handleBankChange}
                className="w-full p-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
              >
                {course && course.own_bank_status === 'approved' ? (
                  <>
                    <option value="bsi">Transfer Bank ({course.own_bank_name})</option>
                    <option value="qris">QRIS Penjual</option>
                  </>
                ) : (
                  <>
                    <option value="bsi">Bank Syariah Indonesia (BSI)</option>
                    <option value="qris">QRIS</option>
                  </>
                )}
              </select>
            </div>

            {/* Bank information card */}
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
              <div className="p-4 flex flex-col items-center">
                <div className="flex items-center w-full mb-4">
                  <img
                    src={selectedBankInfo.logo}
                    alt={selectedBankInfo.name}
                    className="w-12 mr-2"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-bold">{selectedBankInfo.number}</h3>
                      {!selectedBankInfo.isQRIS && (
                        <button
                          onClick={() => copyToClipboard(selectedBankInfo.number, 'Nomor rekening')}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center text-sm"
                        >
                          Salin No Rek.
                        </button>
                      )}
                    </div>
                    <p className="text-gray-600">
                      {selectedBankInfo.isQRIS ? 'Scan QRIS untuk pembayaran' : `a.n. ${selectedBankInfo.owner}`}
                    </p>
                  </div>
                </div>

                {selectedBankInfo.isQRIS && (
                  <div className="w-full flex justify-center p-4 bg-gray-50 rounded-lg">
                    <img
                      src={selectedBankInfo.logo}
                      alt="QRIS"
                      className="max-w-xs w-full shadow-sm rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Amount card */}
            <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
              <div className="p-4">
                <div className="flex items-center mb-2">
                  <div className="flex-1 flex justify-between items-center">
                    <h3 className="text-2xl font-bold">
                      <span className="text-green-500">{formatIDR(course.price)}</span>
                    </h3>
                    <button
                      onClick={() => copyToClipboard(course.price, 'Nominal')}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center text-sm"
                    >
                      Salin Nominal
                    </button>
                  </div>
                </div>
                <div className="bg-yellow-100 text-yellow-800 py-2 px-3 rounded-lg text-sm font-medium">
                  PENTING! Mohon transfer sesuai sampai dengan 3 digit terakhir
                </div>
              </div>
            </div>

            {/* Payment confirmation form */}
            <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
              <div className="p-4">
                <h3 className="text-xl font-bold mb-4">Konfirmasi Pembayaran</h3>
                <form onSubmit={handleSubmit} className="space-y-4 mb-10">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Transfer dari <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="sourceBank"
                      placeholder="Nama Bank Pengirim"
                      className="w-full p-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none mb-2"
                      value={formData.sourceBank}
                      onChange={handleInputChange}
                      required
                    />
                    {!selectedBankInfo.isQRIS && (
                      <input
                        type="text"
                        name="sourceAccount"
                        placeholder="Nomor Rekening Pengirim"
                        className="w-full p-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none mb-2"
                        value={formData.sourceAccount}
                        onChange={handleInputChange}
                        required
                      />
                    )}
                    <input
                      type="text"
                      name="accountName"
                      placeholder="Atas Nama (opsional)"
                      className="w-full p-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none mb-2"
                      value={formData.accountName || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tanggal Transfer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="transferDate"
                      className="w-full p-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                      value={formData.transferDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bukti Transfer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      style={{ opacity: 0, position: 'absolute', zIndex: -1 }}
                      required
                    />
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current.click()}
                    >
                      {previewUrl ? (
                        <div className="relative">
                          <img
                            src={previewUrl}
                            alt="Bukti Transfer"
                            className="max-h-48 mx-auto rounded-lg"
                          />
                          <div className="mt-2 text-sm text-green-600">Klik untuk mengganti</div>
                        </div>
                      ) : (
                        <div>
                          <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <div className="mt-2 text-sm text-gray-600">Klik untuk upload bukti transfer</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
                  >
                    Kirim Konfirmasi
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
      <NavigationButton />
    </div>
  );
};

export default EcoursePaymentConfirmation;